import React, {
  ForwardRefRenderFunction,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import { UploadCSVErrors } from "../../../../../../../../components/Generic/UploadCSVFile";
import { useStorageBoxHook } from "../../../../../../../../hooks/useStorageBoxActions";
import { Branch } from "../../../../../../../../models/branch";
import { StorageBoxDto } from "../../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { Warehouse } from "../../../../../../../../models/warehouse";
import env from "../../../../../../../../util/env";
import readCSV from "../../../../../../../../util/readCSV";
import StorageBoxesImportModal from "../StorageBoxesImportModal";
import {
  StorageBoxTemplateCSV,
  ValidateCSVFieldErrors,
  ValidateStorageBoxesCSV,
} from "./validate.storage.box.csv";

export interface UploadCSVFileHandles {
  open: () => void;
  close: () => void;
}

interface Props {
  ref: RefObject<UploadCSVFileHandles>;
  warehouses: Warehouse[];
  branches: Branch[];
  storageBoxTypes: StorageBoxType[];
}

const UploadStorageBoxModal: ForwardRefRenderFunction<UploadCSVFileHandles, Props> = (
  props,
  ref
) => {
  const { warehouses, branches, storageBoxTypes } = props;
  const modalRef = useRef<UploadCSVFileHandles>(null);
  const { t: translate } = useTranslation();

  const [lineErrorsCSV, setLineErrorsCSV] = useState<UploadCSVErrors>({});
  const [generalErrorsCSV, setGeneralErrorsCSV] = useState<UploadCSVErrors>({});
  const errors: string[] = [];

  const { useCreateStorageBoxesList, hasErrorUpload } = useStorageBoxHook();

  const { mutate: createStorageBoxes, isLoading } = useCreateStorageBoxesList();

  const openModal = useCallback(() => {
    modalRef.current?.open();
  }, []);
  const closeModal = useCallback(() => {
    modalRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  useEffect(() => {
    if (hasErrorUpload !== undefined && !hasErrorUpload) {
      closeModal();
    }
  }, [hasErrorUpload]);

  const getStorageBoxTypeId = (storageBoxTypeName: string) => {
    const storageBoxType = storageBoxTypes.find(
      (storageBoxType) => storageBoxType?.name?.toLowerCase() === storageBoxTypeName?.toLowerCase()
    );

    return storageBoxType;
  };

  const getBranchId = (branchName: string) => {
    const branch = branches.find(
      (branch) =>
        branch?.name?.toLowerCase() === branchName?.toLowerCase() ||
        branch?.code?.toLowerCase() === branchName?.toLowerCase()
    );

    return branch?.id;
  };

  const getWarehousesIds = (warehousesNames: string) => {
    const storageBoxesWarehousesIds: number[] = [];

    warehousesNames.split("|").forEach((warehouseName) => {
      const foundWarehouse = warehouses.find(
        (warehouse) =>
          warehouse?.name?.toLowerCase() === warehouseName?.trim()?.toLowerCase() ||
          warehouse?.code?.toLowerCase() === warehouseName?.trim()?.toLowerCase()
      );

      if (foundWarehouse && foundWarehouse?.id) {
        storageBoxesWarehousesIds.push(foundWarehouse?.id);
      }
    });

    return storageBoxesWarehousesIds;
  };

  const getBelongsToWarehouseId = (belongsToWarehouse: string) => {
    const warehouse = warehouses?.find(
      (warehouse) =>
        warehouse?.name?.toLowerCase() === belongsToWarehouse?.toLowerCase() ||
        warehouse?.code?.toLowerCase() === belongsToWarehouse?.toLowerCase()
    );

    return warehouse?.id || 0;
  };

  const amountStorageBoxes = (storageBoxes: StorageBoxTemplateCSV[]) => {
    const amountedStorageBoxes = storageBoxes.map((storageBox) => {
      return {
        number: storageBox.number,
        storageBoxTypeId: getStorageBoxTypeId(storageBox.storageBoxType)?.id,
        branchesIds: storageBox.branch ? [getBranchId(storageBox.branch)] : [],
        sharedWarehousesIds: getWarehousesIds(storageBox.sharedWarehouses),
        belongsToWarehouseId: getBelongsToWarehouseId(storageBox?.belongsToWarehouse),
      } as StorageBoxDto;
    });

    return amountedStorageBoxes;
  };

  const onConfirmUploadCSV = async (data: StorageBoxTemplateCSV[]) => {
    if (!data || !data.length) {
      return NotificationToast.warning(translate("thereAreNoRecordsInThisFile"), {
        id: "warning-file-empty",
      });
    }

    const lineErrors: ValidateCSVFieldErrors = {};
    const csvGlobalErrors: UploadCSVErrors = {};

    const { valid } = ValidateStorageBoxesCSV({
      branches,
      warehouses,
      storageBoxTypes,
      data,
      translate,
      lineErrors,
    });

    const linesIndex = Object.keys(lineErrors);

    linesIndex.forEach((line) => {
      if (lineErrors[line].length > 0) {
        lineErrors[line].forEach((error) => {
          const message = `Line ${line} - ${error}`;

          errors.push(message);
        });
      }
    });

    if (!valid || !!Object.keys(csvGlobalErrors).length) {
      setLineErrorsCSV(lineErrors);
      setGeneralErrorsCSV(csvGlobalErrors);
    }

    if (errors.length === 0 && valid) {
      const storageBoxesFormatted = amountStorageBoxes(data);

      createStorageBoxes({ storageBoxes: storageBoxesFormatted });
    }
  };

  return (
    <StorageBoxesImportModal
      ref={modalRef}
      lineErrors={lineErrorsCSV}
      onConfirmUpload={(uri) => {
        readCSV<StorageBoxTemplateCSV>(
          uri,
          ["number", "storageBoxType", "branch", "sharedWarehouses", "belongsToWarehouse"],
          (data) => onConfirmUploadCSV(data)
        );
      }}
      generalErrors={generalErrorsCSV}
      onTryAgain={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
      }}
      onCloseModal={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
      }}
      onDownloadTemplate={() => window.open(env.LINK_TEMPLATE_STORAGE_BOX_CSV, "_blank")?.focus()}
      loadingUpload={isLoading}
    />
  );
};

export default React.forwardRef(UploadStorageBoxModal);
